export type UsageMetricRow = {
  company: string;
  date: string;
  clients: number;
  srs: number;
  notes: number;
  agendas: number;
  recordings: number;
  files: number;
};

export type UsageMetricKey = 'clients' | 'srs' | 'notes' | 'agendas' | 'recordings' | 'files';

export const USAGE_METRIC_KEYS: readonly UsageMetricKey[] = [
  'clients',
  'srs',
  'notes',
  'agendas',
  'recordings',
  'files',
];

export const METRIC_LABELS: Record<UsageMetricKey, string> = {
  clients: 'New clients',
  srs: 'SR creates',
  notes: 'Meeting notes',
  agendas: 'Agendas',
  recordings: 'Meetings recorded',
  files: 'Files uploaded',
};

export type MetricOption = 'total' | UsageMetricKey;

export const METRIC_OPTIONS: readonly { value: MetricOption; label: string }[] = [
  { value: 'total', label: 'Total actions' },
  ...USAGE_METRIC_KEYS.map(key => ({ value: key as MetricOption, label: METRIC_LABELS[key] })),
];

export type Period = 'week' | 'month';

type MetricTotals = Record<UsageMetricKey, number>;

function emptyTotals(): MetricTotals {
  return { clients: 0, srs: 0, notes: 0, agendas: 0, recordings: 0, files: 0 };
}

function addRow(totals: MetricTotals, row: UsageMetricRow) {
  for (const key of USAGE_METRIC_KEYS) totals[key] += row[key];
}

function totalOf(totals: MetricTotals) {
  return USAGE_METRIC_KEYS.reduce((sum, key) => sum + totals[key], 0);
}

/**
 * Thursday-of-the-week ISO 8601 algorithm: a week belongs to the year containing its Thursday,
 * so weeks near a year boundary land in the correct ISO year even though the calendar date does not.
 */
function isoWeekInfo(date: Date): { isoYear: number; isoWeek: number; weekStart: Date } {
  const base = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = base.getUTCDay() || 7;

  const weekStart = new Date(base);
  weekStart.setUTCDate(base.getUTCDate() - dayNum + 1);

  const thursday = new Date(base);
  thursday.setUTCDate(base.getUTCDate() + 4 - dayNum);
  const isoYear = thursday.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const isoWeek = Math.ceil(((thursday.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);

  return { isoYear, isoWeek, weekStart };
}

function toIsoDateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

type PeriodInfo = { key: string; label: string; start: string };

function periodInfo(dateString: string, period: Period): PeriodInfo {
  if (period === 'month') {
    const key = dateString.slice(0, 7);
    const label = new Date(`${key}-01T00:00:00.000Z`).toLocaleDateString('en-GB', {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
    return { key, label, start: `${key}-01` };
  }

  const date = new Date(`${dateString}T00:00:00.000Z`);
  const { isoYear, isoWeek, weekStart } = isoWeekInfo(date);
  const key = `${isoYear}-W${String(isoWeek).padStart(2, '0')}`;
  const label = weekStart.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return { key, label, start: toIsoDateString(weekStart) };
}

type PeriodPoint = {
  periodKey: string;
  periodLabel: string;
  periodStart: string;
  totals: MetricTotals;
  total: number;
};

/** Platform-wide totals, one point per period, sorted chronologically. */
export function aggregatePlatformTotals(
  rows: readonly UsageMetricRow[],
  period: Period,
): PeriodPoint[] {
  const buckets = new Map<string, { label: string; start: string; totals: MetricTotals }>();

  for (const row of rows) {
    const { key, label, start } = periodInfo(row.date, period);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { label, start, totals: emptyTotals() };
      buckets.set(key, bucket);
    }
    addRow(bucket.totals, row);
  }

  return [...buckets.entries()]
    .map(([periodKey, bucket]) => ({
      periodKey,
      periodLabel: bucket.label,
      periodStart: bucket.start,
      totals: bucket.totals,
      total: totalOf(bucket.totals),
    }))
    .sort((a, b) => a.periodStart.localeCompare(b.periodStart));
}

type CompanyPeriodPoint = PeriodPoint & { company: string };

/** One point per company per period, sorted by company then chronologically. */
export function aggregatePerCompany(
  rows: readonly UsageMetricRow[],
  period: Period,
): CompanyPeriodPoint[] {
  const buckets = new Map<
    string,
    { company: string; periodKey: string; label: string; start: string; totals: MetricTotals }
  >();

  for (const row of rows) {
    const { key, label, start } = periodInfo(row.date, period);
    const bucketId = `${row.company} ${key}`;
    let bucket = buckets.get(bucketId);
    if (!bucket) {
      bucket = { company: row.company, periodKey: key, label, start, totals: emptyTotals() };
      buckets.set(bucketId, bucket);
    }
    addRow(bucket.totals, row);
  }

  return [...buckets.values()]
    .map(bucket => ({
      company: bucket.company,
      periodKey: bucket.periodKey,
      periodLabel: bucket.label,
      periodStart: bucket.start,
      totals: bucket.totals,
      total: totalOf(bucket.totals),
    }))
    .sort(
      (a, b) => a.company.localeCompare(b.company) || a.periodStart.localeCompare(b.periodStart),
    );
}

type ActiveCompaniesPoint = {
  periodKey: string;
  periodLabel: string;
  periodStart: string;
  activeCompanies: number;
  highActivityCompanies: number;
};

const HIGH_ACTIVITY_THRESHOLD = 10;

/** How many companies did anything (>=1 action), and how many did a lot (>10 actions), per period. */
export function aggregateActiveCompanies(
  rows: readonly UsageMetricRow[],
  period: Period,
): ActiveCompaniesPoint[] {
  const perCompany = aggregatePerCompany(rows, period);
  const buckets = new Map<string, { label: string; start: string; active: number; high: number }>();

  for (const point of perCompany) {
    if (point.total <= 0) continue;
    let bucket = buckets.get(point.periodKey);
    if (!bucket) {
      bucket = { label: point.periodLabel, start: point.periodStart, active: 0, high: 0 };
      buckets.set(point.periodKey, bucket);
    }
    bucket.active += 1;
    if (point.total > HIGH_ACTIVITY_THRESHOLD) bucket.high += 1;
  }

  return [...buckets.entries()]
    .map(([periodKey, bucket]) => ({
      periodKey,
      periodLabel: bucket.label,
      periodStart: bucket.start,
      activeCompanies: bucket.active,
      highActivityCompanies: bucket.high,
    }))
    .sort((a, b) => a.periodStart.localeCompare(b.periodStart));
}

type GrowthResult = { kind: 'percent'; value: number } | { kind: 'new' } | { kind: 'none' };

export function formatGrowth(growth: GrowthResult): string {
  switch (growth.kind) {
    case 'none':
      return '—';
    case 'new':
      return 'new';
    case 'percent':
      return `${growth.value >= 0 ? '+' : ''}${growth.value.toFixed(0)}%`;
  }
}

export type CompanySummary = {
  company: string;
  firstActiveMonth: string | null;
  lastActiveMonth: string | null;
  activeMonthsCount: number;
  totals: MetricTotals;
  grandTotal: number;
  last3MonthsTotal: number;
  prior3MonthsTotal: number;
  growth: GrowthResult;
};

function shiftMonthKey(monthKey: string, delta: number): string {
  const [year, month] = monthKey.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1 + delta, 1));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * "Last 3 months" and "prior 3 months" are anchored to the latest month present anywhere in the
 * dataset, not the caller's wall-clock time, so the growth comparison stays identical across
 * companies and is deterministic for tests.
 */
export function summariseCompanies(rows: readonly UsageMetricRow[]): CompanySummary[] {
  if (rows.length === 0) return [];

  const monthlyByCompany = aggregatePerCompany(rows, 'month');
  const latestMonthKey = monthlyByCompany.reduce(
    (latest, point) => (point.periodKey > latest ? point.periodKey : latest),
    monthlyByCompany[0].periodKey,
  );

  const last3Keys = [0, 1, 2].map(delta => shiftMonthKey(latestMonthKey, -delta));
  const prior3Keys = [3, 4, 5].map(delta => shiftMonthKey(latestMonthKey, -delta));

  const companies = [...new Set(rows.map(row => row.company))].sort((a, b) => a.localeCompare(b));

  return companies.map(company => {
    const points = monthlyByCompany.filter(point => point.company === company);
    const activeMonths = points
      .filter(point => point.total > 0)
      .map(point => point.periodKey)
      .sort();

    const totals = emptyTotals();
    for (const point of points) {
      for (const key of USAGE_METRIC_KEYS) totals[key] += point.totals[key];
    }

    const last3MonthsTotal = points
      .filter(point => last3Keys.includes(point.periodKey))
      .reduce((sum, point) => sum + point.total, 0);
    const prior3MonthsTotal = points
      .filter(point => prior3Keys.includes(point.periodKey))
      .reduce((sum, point) => sum + point.total, 0);

    let growth: GrowthResult;
    if (last3MonthsTotal === 0 && prior3MonthsTotal === 0) {
      growth = { kind: 'none' };
    } else if (prior3MonthsTotal === 0) {
      growth = { kind: 'new' };
    } else {
      growth = {
        kind: 'percent',
        value: ((last3MonthsTotal - prior3MonthsTotal) / prior3MonthsTotal) * 100,
      };
    }

    return {
      company,
      firstActiveMonth: activeMonths[0] ?? null,
      lastActiveMonth: activeMonths[activeMonths.length - 1] ?? null,
      activeMonthsCount: activeMonths.length,
      totals,
      grandTotal: totalOf(totals),
      last3MonthsTotal,
      prior3MonthsTotal,
      growth,
    };
  });
}
